import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { JsonLd } from "react-schemaorg";
import type { Language, Person, Thing } from "schema-dts";

import { getAssetPrefix } from "shared/lib/utils";

import type { authorLinkingDataAuthor$key } from "scmp-app/queries/__generated__/authorLinkingDataAuthor.graphql";

type Props = {
  reference: authorLinkingDataAuthor$key;
};

export const AuthorLinkingData: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const author = useFragment(
    graphql`
      fragment authorLinkingDataAuthor on Author {
        bio
        areaOfExpertise
        urlAlias
        name
        image320x320: image(filter: { type: AUTHOR_IMAGE }) {
          style(filter: { style: "320x320" }) {
            url
          }
        }
        languageSpoken
        location
        role {
          text
        }
      }
    `,
    reference_,
  );

  return (
    <JsonLd<Person>
      item={{
        "@context": "https://schema.org",
        "@type": "Person",
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "Newsroom contact",
            email: "news@scmp.com",
            telephone: "+852 2565 2222",
            url: "https://www.scmp.com/contact-us",
          },
        ],
        description: author.bio ?? "",
        identifier: author.urlAlias.match("/([^/]+)/?$")?.[1] ?? "",
        image: author.image320x320?.style?.url ?? "",
        jobTitle: author.role?.text?.replace(/[\n\r]/g, "") ?? "",
        knowsAbout: author.areaOfExpertise
          ?.flatMap(expertise => expertise?.split(",") ?? [])
          .map(
            expertise =>
              ({
                "@type": "Thing",
                name: expertise,
              }) as Thing,
          ),
        knowsLanguage:
          author.languageSpoken?.map(
            language =>
              ({
                "@type": "Language",
                name: language,
              }) as Language,
          ) ?? [],
        name: author.name,
        url: author.urlAlias,
        workLocation: {
          "@type": "Place",
          name: author.location ?? "",
        },
        worksFor: {
          "@type": "Organization",
          logo: {
            "@type": "ImageObject",
            height: "60",
            url: `${getAssetPrefix()}/icons/scmp-icon-512x512.png`,
            width: "600",
          },
          name: "South China Morning Post",
        },
      }}
    />
  );
};

AuthorLinkingData.displayName = "AuthorLinkingData";
